import React, { useEffect } from 'react';
import { Github, Linkedin } from 'lucide-react';
import { SiX } from 'react-icons/si';
import { useLocation } from 'react-router-dom';
import Contacts from './Contacts';
import ProjectCard from './ProjectCard';
import { profile, projects } from '../data/projects';

export default function Homepage() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return undefined;
    const sectionId = location.hash.slice(1);
    const frame = window.requestAnimationFrame(() => {
      document.getElementById(sectionId)?.scrollIntoView({ block: 'start' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.hash]);

  return <main>
    <section className="hero page-shell" aria-labelledby="hero-title">
      <div className="hero-copy">
        <p className="eyebrow"> Software developer · Kenya</p>
        <h1 id="hero-title">I build web applications for business workflows.</h1>
        <p className="hero-summary">I turn messy processes into clear, dependable products using React, Python, and thoughtful APIs. The work below shows how I approach interfaces, data, and the systems behind them.</p>
        <div className="hero-actions">
          <a className="button button-primary" href="#work">View selected work <span aria-hidden="true">↓</span></a>
          <a className="button button-secondary" href={`mailto:${profile.email}`}>Email me <span aria-hidden="true">→</span></a>
        </div>
      </div>
      <aside className="hero-note" aria-label="Profile details">
        <div><span>Based in</span><strong>{profile.location}</strong></div>
        <div><span>Currently studying</span><strong>Software engineering</strong></div>
        <div><span>Interested in</span><strong>Products, APIs, automation</strong></div>
        <div className="hero-socials">
          <a href={profile.github} aria-label="GitHub" target="_blank" rel="noreferrer"><Github size={19} /></a>
          <a href="https://www.linkedin.com/in/muhammad-ndurya-8b7318288/" aria-label="LinkedIn" target="_blank" rel="noreferrer"><Linkedin size={19} /></a>
          <a href={profile.x} aria-label="X" target="_blank" rel="noreferrer"><SiX size={18} /></a>
        </div>
      </aside>
    </section>

    <section className="work-section page-shell" id="work" aria-labelledby="work-title">
      <div className="section-heading">
        <div><p className="eyebrow">Selected work</p><h2 id="work-title">Systems with a job to do.</h2></div>
        <p className="section-intro">A small selection of products and backend systems, documented around the decisions that make them useful.</p>
      </div>
      <div className="project-list">{projects.map((project) => <ProjectCard project={project} key={project.slug} />)}</div>
    </section>

    <section className="about-section page-shell" id="about" aria-labelledby="about-title">
      <div><p className="eyebrow">How I work</p><h2 id="about-title">Curious about the whole system.</h2></div>
      <div className="about-copy">
        <p>I’m a software engineering student and full-stack developer based in Kenya. I enjoy taking a vague idea, finding the important workflow inside it, and building the interface and backend that make the workflow easier to trust.</p>
        <p>My current toolkit includes React, JavaScript, Python, Django, FastAPI, PostgreSQL, Docker, and the practical debugging that connects them. I care about clear boundaries, accessible interfaces, and documentation that another person can actually use.</p>
        <div className="capability-grid">
          <div><span className="eyebrow">01</span><strong>Interfaces</strong><p>Responsive React screens with useful loading, empty, and error states.</p></div>
          <div><span className="eyebrow">02</span><strong>APIs &amp; data</strong><p>Validated endpoints, relational models, ownership rules, and reliable workflows.</p></div>
          <div><span className="eyebrow">03</span><strong>Delivery</strong><p>Docker-ready systems, deployment notes, and a bias toward simple operations.</p></div>
        </div>
      </div>
    </section>

    <div className="page-shell"><Contacts /></div>
    <footer className="site-footer page-shell"><span>© {new Date().getFullYear()} {profile.name}</span></footer>
  </main>;
}
